import { db } from './db';
import type { OutboxItem, OutboxKind } from './types';

/**
 * Изходящата опашка. Всеки ред сочи към запис в своята таблица; полезният
 * товар се сглобява чак при изпращане, за да няма два източника на истина.
 *
 * Опашката е видима за шофьора („Чакащи") — това е разликата между
 * „приложението може да работи офлайн" и „шофьорът вярва, че е изпратено".
 */

/** 5 с, 15 с, 45 с, 2 мин, 6 мин, 15 мин, после на всеки 30 мин. */
export function backoffMs(attempts: number): number {
  const base = Math.min(30 * 60_000, 5_000 * 3 ** Math.max(0, attempts - 1));
  const jitter = Math.random() * 0.25 * base;
  return Math.round(base + jitter);
}

/**
 * `delayMs` дава малка отсрочка на записите, към които тепърва ще се закачи
 * координата — иначе събитието тръгва празно, а точката пристига след него.
 */
export async function enqueue(
  kind: OutboxKind,
  refId: string,
  label: string,
  delayMs = 0,
): Promise<void> {
  if (await db.outbox.get(refId)) return;
  const item: OutboxItem = {
    id: refId,
    kind,
    refId,
    label,
    createdAt: Date.now(),
    attempts: 0,
    nextAttemptAt: delayMs > 0 ? Date.now() + delayMs : 0,
    status: 'pending',
    lastError: null,
    sentAt: null,
  };
  await db.outbox.put(item);
}

export async function markSent(id: string): Promise<void> {
  await db.outbox.update(id, { status: 'done', sentAt: Date.now(), lastError: null });
}

export async function markFailed(id: string, error: string, permanent: boolean): Promise<void> {
  const item = await db.outbox.get(id);
  const attempts = (item?.attempts ?? 0) + 1;
  await db.outbox.update(id, {
    attempts,
    status: permanent ? 'blocked' : 'pending',
    lastError: error,
    nextAttemptAt: permanent ? 0 : Date.now() + backoffMs(attempts),
  });
}

export interface QueueSummary {
  pending: number;
  blocked: number;
  photos: number;
}

export async function queueSummary(): Promise<QueueSummary> {
  const items = await db.outbox.where('status').anyOf('pending', 'sending').toArray();
  const blocked = await db.outbox.where('status').equals('blocked').count();
  return {
    pending: items.length,
    blocked,
    photos: items.filter((item) => item.kind === 'photo').length,
  };
}

/** Изпратените редове се пазят два дни, за да се вижда „изпратено", после си отиват. */
export async function pruneSentItems(): Promise<void> {
  const cutoff = Date.now() - 2 * 24 * 60 * 60_000;
  const stale = await db.outbox.where('status').equals('done').toArray();
  const ids = stale.filter((item) => (item.sentAt ?? 0) < cutoff).map((item) => item.id);
  if (ids.length > 0) await db.outbox.bulkDelete(ids);

  // Качените снимки заемат мегабайти — самият файл се трие, метаданните остават.
  const uploaded = await db.photos.where('status').equals('done').toArray();
  const heavy = uploaded.filter(
    (photo) => photo.blob !== null && new Date(photo.capturedAt).getTime() < cutoff,
  );
  for (const photo of heavy) await db.photos.update(photo.id, { blob: null });
}
